import React, { useState } from 'react';
import { Occupations } from './occupations';
import { Skills,SkillCategoryData } from './skills';
import { Others } from './others';

interface FormData {
  occupations: number[];
  skills: number[];
  workStyles: number[];
  prefId: number | null;
  payMin: number | null;
  payMax: number | null;
}

type PanelName = 'occupation' | 'skill' | 'other';

export const Main: React.FC = () => {

  const initialFormData: FormData = {
    occupations: [],
    skills: [],
    workStyles: [],
    prefId: null,
    payMin: null,
    payMax: null,
  };
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [currentPanel, setCurrentPanel] = useState<PanelName>('occupation');
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [skillsData, setSkillsData] = useState<SkillCategoryData[]>([]); // スキルデータを管理
  const [loading, setLoading] = useState<boolean>(false); // ローディング状態を管理
  const [error, setError] = useState<string | null>(null); // エラーメッセージを管理

  const handleSkillsLoad = (data: SkillCategoryData[]) => {
    setSkillsData(data);
  };
    
  const togglePanel = () => {
    setIsVisible((prev) => {
      if (!prev) {
        document.body.style.overflow = 'hidden';
        return true;
      }

      document.body.style.overflow = '';
      setFormData(initialFormData);
      setCurrentPanel('occupation');
      return false;
    });
  };

  const handleFormDataChange = (key: string, value: any) => {
    setFormData({ ...formData, [key]: value });
  };

  const handleNext = (nextPanel: PanelName) => {
    setCurrentPanel(nextPanel);
  };

  const handleBack = (previousPanel: PanelName) => {
    setCurrentPanel(previousPanel);
  };

  const toSnakeCase = (str: string): string  =>
    str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

  const handleSubmit = () => {
    // フィルタリング
    const filteredData = Object.fromEntries(
      Object.entries(formData).filter(([_, value]) => {
        if (value === null) return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      })
    );

    // 職種が選択されて、スキルが未選択の場合、選択肢に上がったスキル全てで検索する
    // (formData において、skills が空の場合、skillsData からスキルIDを取得)
    if (formData.occupations.length !== 0) {
      if (formData.skills.length === 0 && skillsData.length > 0) {
        const skill_ids = skillsData.flatMap((category) =>
          category.skills.map((skill) => skill.id)
        );
        filteredData.skills = skill_ids;
      }
      // 検索対象となるスキルは、選択肢に上がったスキルに含まれるもののみとする
      if (filteredData.skills && skillsData.length > 0) {
        const validSkillIds = filteredData.skills.filter((id: number) =>
          skillsData.some((category) =>
            category.skills.some((skill) => skill.id === id)
          )
        );
        filteredData.skills = validSkillIds;
      }
    }

    // キャメルケースをスネークケースに変換しつつ配列データを処理
    const params = new URLSearchParams(
      Object.entries(filteredData).flatMap(([key, value]) =>
        Array.isArray(value)
          ? value.map((v) => [`${toSnakeCase(key)}[]`, v]) // 配列をスネークケースの key[]=value に変換
          : [[toSnakeCase(key), value]]
      )
    ).toString();

    // 検索URLにリダイレクト
    location.href = `/projects?${params}`
  };

  const fetchData = async (url: string): Promise<any> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('エラーが発生しました。しばらく時間をおいてからアクセスしてください。');
      }
      return await response.json();
    } catch (err: any) {
      setError(err.message || 'エラーが発生しました。しばらく時間をおいてからアクセスしてください。');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const commonProps = {
    togglePanel,
    formData,
    onFormDataChange: handleFormDataChange,
    fetchData,
    loading,
    error,
  };

  const panels = {
    occupation: (
      <Occupations
        {...commonProps}
        onNext={() => handleNext('skill')}
      />
    ),
    skill: (
      <Skills
        {...commonProps}
        onSubmit={handleSubmit}
        onNext={() => handleNext('other')}
        onBack={() => handleBack('occupation')}
        onSkillsLoad={handleSkillsLoad}
      />
    ),
    other: (
      <Others
        {...commonProps}
        onSubmit={handleSubmit}
        onBack={() => handleBack('skill')}
      />
    ),
  };

  return (
    <div>
      <div className="p-searchButton__wrapper">
        <button
          className="p-searchPanel__searchButton"
          onClick={togglePanel}
        >
          <span>案件を検索する</span>
          <img
            src="/asset/img/common/magnifying-glass-white.svg"
            alt="検索アイコン"
            className="p-searchPanel__searchButton__icon"
          />
        </button>
      </div>
      {isVisible && <>{panels[currentPanel]}</>}
    </div>
  );
};